import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { useScale, usePlaySound } from "common/utils";
import { HintIcon } from "features/courses/assets";

const HINT_LEVELS = [
  "MSG_BUILDER_DETAIL_HINT_LV1",
  "MSG_BUILDER_DETAIL_HINT_LV2",
  "MSG_BUILDER_DETAIL_HINT_LV3",
];

// ピースの段階別ヒント（①やさしい/②ふつう/③こたえ）。
// アルゴリズムをまなぼの GuidePage と同じ「右端の縦書きタブ + スライドで開くページ」形式。
// 各レベルはタップで開閉（こたえの見すぎ防止に初期は閉じた状態）。
// ヒント未設定のピースではタブごと出さない。
export const PreviewHintPanel = () => {
  const play = usePlaySound();
  const { height, scale } = useScale();
  const [collapse, setCollapse] = useState(true);
  const [opened, setOpened] = useState<boolean[]>([false, false, false]);
  const hint = useSelector((state: RootState) => state.builder.hint);

  if (!hint.some((h) => h?.trim())) return null;

  const COLLAPSE_BTN_WIDTH = 50;
  const PANEL_WIDTH = 640;

  const handleCollapse = () => {
    play();
    setCollapse(!collapse);
  };

  const handleToggleLevel = (index: number) => () => {
    play();
    setOpened((prev) => prev.map((v, i) => (i === index ? !v : v)));
  };

  return (
    <div
      className="flex-col-view !absolute top-0 z-[9998] pointer-events-auto h-full transition-transform duration-500"
      style={{
        width: PANEL_WIDTH * scale,
        right: -PANEL_WIDTH * scale,
        transform: `translateX(${collapse ? 0 : -PANEL_WIDTH * scale}px)`,
      }}
    >
      {/* 縦書きヒントタブ（GuidePage と同じ見た目） */}
      <div
        className="flex-col-view z-[100] cursor-pointer pointer-events-auto !absolute"
        style={{
          left: -COLLAPSE_BTN_WIDTH * scale,
          width: COLLAPSE_BTN_WIDTH * scale,
          top: 200 * scale,
          height: 200 * scale,
          filter: "drop-shadow(-1px 1px 2px rgba(63, 74, 97, 0.4))",
        }}
        onClick={handleCollapse}
      >
        <div
          className="flex-col-view h-[200px] items-center origin-top-left bg-gray2-20 border-white rounded-l-2xl border-solid border-[5px] border-r-0 p-2"
          style={{
            width: COLLAPSE_BTN_WIDTH,
            transform: `scale(${scale})`,
          }}
        >
          <div className="flex-col-center w-[32px] h-[32px] mb-2">
            <HintIcon />
          </div>

          <p className="title-text text-vertical type-body leading-6">
            {I18n.t("MSG_COURSE_GAME_HINT_BTN")}
          </p>
        </div>
      </div>

      {/* ページ本体（GuidePage と同じ背景画像） */}
      <div
        className="flex-col-view items-center justify-start h-full"
        style={{ width: PANEL_WIDTH * scale }}
      >
        <img
          alt="*"
          className="w-full h-full flex-col-view !absolute top-0 left-0 z-[-1] object-cover"
          src={`${Constants.assetHost}/assets/images/bg_course_guide.png`}
        />

        <div
          className="flex-col-el items-center justify-start origin-top w-full gap-3 p-8 box-border"
          style={{
            width: PANEL_WIDTH,
            height: height / scale,
            transform: `scale(${scale})`,
          }}
        >
          <div className="flex-row-el items-center gap-2">
            <div className="flex-col-center w-[32px] h-[32px]">
              <HintIcon />
            </div>
            <p className="type-text type-title text-textcolor-black">
              {I18n.t("MSG_BUILDER_DETAIL_HINT_SECTION")}
            </p>
          </div>

          {HINT_LEVELS.map((lv, i) =>
            hint[i]?.trim() ? (
              <button
                key={lv}
                type="button"
                onClick={handleToggleLevel(i)}
                className="type-inherit-font flex-col-el items-start w-full gap-1 rounded-xl bg-white/90 p-4 border-none shadow-md cursor-pointer"
              >
                <div className="flex-row-el items-center justify-between w-full">
                  <p className="type-text type-body text-textcolor-black/70">{I18n.t(lv)}</p>
                  <span className="material-symbols-outlined type-md text-textcolor-black/50">
                    {opened[i] ? "expand_less" : "expand_more"}
                  </span>
                </div>
                {opened[i] && (
                  <p className="type-text-left type-body text-textcolor-black w-full">{hint[i]}</p>
                )}
              </button>
            ) : null,
          )}
        </div>
      </div>
    </div>
  );
};
